import type React from 'react';

import { Badge, Text } from '@change/ds-core-ui';

import { TranslatePlural } from '@change-corgi/core-react/i18n';

import type { VideoStory } from '#src/app/pages/about/impact/pageContext';

type Props = Readonly<{
	story: VideoStory;
	isActive: boolean;
	reloadCount: number;
}>;

/**
 * The embed is in the markup from the first render and marked for lazy loading, so the browser
 * fetches the player once the slide nears the viewport. An inactive slide's frame is inert, so its
 * player is out of the focus order and a click on it falls through to the slide, which selects it;
 * bumping `reloadCount` remounts the iframe, which is what stops playback.
 */
export const VideoSlide: React.FC<Props> = ({ story, isActive, reloadCount }) => {
	return (
		<div className="flex flex-col gap-4">
			<div
				inert={!isActive}
				className="bg-typography-strong-fixed relative aspect-video overflow-hidden rounded-2xl"
				data-qa="impact-video-slide-frame"
			>
				<iframe
					key={reloadCount}
					src={story.videoUrl}
					title={story.title}
					loading="lazy"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					referrerPolicy="strict-origin-when-cross-origin"
					allowFullScreen
					className="absolute inset-0 h-full w-full"
				/>
			</div>
			<div className="flex flex-col items-start gap-2">
				<Badge
					variant="information"
					emphasis="strong"
					size="medium"
					label={
						<TranslatePlural
							value="corgi.impact_page.petition_starters.signature_count"
							count={story.signatureCount}
							replacements={{ countLocalized: story.signatureCountLocalized }}
						/>
					}
				/>
				<Text as="h3" variant="heading" size="small" fontWeight="bold">
					{story.title}
				</Text>
			</div>
		</div>
	);
};
